import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Home,
  ArrowLeft,
  MessageCircle,
  PhoneCall,
  Facebook,
  Instagram,
  Video,
  Search,
} from "lucide-react";
import logo from "../assets/logo/smartbusiness-white1.png";
import { usePublicLanguage } from "../hooks/usePublicLanguage";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

export default function Error404() {
  const navigate = useNavigate();

  const { lang, setLang, t, isRTL, ready } = usePublicLanguage({
    apiBase: API_BASE,
    defaultLang: "ar",
  });

  const SOCIAL = {
    facebook: "https://facebook.com",
    instagram: "https://instagram.com",
    tiktok: "https://tiktok.com",
    whatsapp: "https://wa.me/972524657778",
  };

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="bg-white border rounded-2xl shadow-sm p-6">
          <div className="text-lg font-bold text-slate-900">Loading…</div>
          <div className="text-slate-600 mt-1">Preparing language</div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col bg-gradient-to-b from-slate-100 via-slate-50 to-slate-100 text-slate-900 overflow-x-hidden relative"
      dir={isRTL ? "rtl" : "ltr"}
    >
      {/* subtle background */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(59,130,246,0.03),transparent_72%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom,rgba(34,211,238,0.025),transparent_72%)]" />
        <div className="hidden sm:block absolute inset-0 opacity-[0.01] bg-[linear-gradient(to_right,rgba(15,23,42,0.16)_1px,transparent_1px),linear-gradient(to_bottom,rgba(15,23,42,0.16)_1px,transparent_1px)] bg-[size:110px_110px]" />
      </div>

      {/* NAVBAR */}
      <nav className="sticky top-0 z-50 border-b border-slate-800 bg-slate-950/90 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-16 flex items-center justify-between gap-3 min-w-0">
            <button type="button" onClick={() => navigate("/")} className="flex items-center gap-3 min-w-0">
              <div className="h-9 w-9 shrink-0 rounded-2xl bg-slate-900 border border-slate-800 flex items-center justify-center shadow-sm">
                <img src={logo} alt="SmartBusiness" className="h-5 w-auto" />
              </div>
              <span className="text-base sm:text-lg font-semibold tracking-tight text-slate-100 truncate">
                {t("homepage.brand", "SmartBusiness")}
              </span>
            </button>

            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              {/* Language Switcher */}
              <div className="flex items-center gap-1 rounded-xl bg-slate-900/70 p-1 border border-slate-800 shrink-0">
                {["ar", "en", "he"].map((language) => (
                  <button
                    key={language}
                    onClick={() => setLang(language)}
                    className={`px-3 py-1.5 text-xs sm:text-sm rounded-lg transition-all ${
                      lang === language
                        ? "bg-slate-100 text-slate-950 shadow-sm"
                        : "text-slate-300 hover:text-slate-100 hover:bg-slate-800/70"
                    }`}
                  >
                    {language === "ar" ? "العربية" : language === "en" ? "EN" : "HE"}
                  </button>
                ))}
              </div>

              <button
                onClick={() => navigate("/")}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-100 text-slate-950 font-semibold shadow-sm hover:bg-white transition focus:outline-none focus:ring-4 focus:ring-slate-400/40 shrink-0"
              >
                <Home className="h-4 w-4" />
                <span className="hidden sm:inline">{t("error.backHome", "Home")}</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* MAIN */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 py-12 sm:py-16">
        <div className="max-w-2xl w-full">
          <div className="relative">
            <div className="absolute -top-12 -left-12 h-32 w-32 rounded-full bg-cyan-200/20 blur-3xl" />
            <div className="absolute -bottom-12 -right-12 h-32 w-32 rounded-full bg-blue-200/20 blur-3xl" />

            <div className="relative rounded-3xl border border-slate-200 bg-white/80 backdrop-blur-sm shadow-xl overflow-hidden">
              <div className="h-2 bg-gradient-to-r from-cyan-500 to-blue-600" />

              <div className="p-8 sm:p-12 text-center">
                <div className="text-8xl sm:text-9xl font-bold leading-none">
                  <span className="bg-gradient-to-r from-blue-700 to-cyan-700 bg-clip-text text-transparent">404</span>
                </div>

                <div className="mt-6 flex justify-center">
                  <div className="h-20 w-20 rounded-2xl bg-slate-100 border border-slate-200 flex items-center justify-center">
                    <AlertTriangle className="h-10 w-10 text-amber-500" />
                  </div>
                </div>

                <h1 className="mt-6 text-2xl sm:text-3xl font-bold text-slate-900">
                  {t("error.title", "Page Not Found")}
                </h1>

                <p className="mt-3 text-slate-600 max-w-md mx-auto">
                  {t(
                    "error.description",
                    "The page you're looking for doesn't exist or may have been moved. Let's get you back on track."
                  )}
                </p>

                <div className="mt-8 flex items-center justify-center gap-2 text-sm text-slate-500">
                  <Search className="h-4 w-4" />
                  <span>{t("error.suggestion", "Try checking the URL or return home")}</span>
                </div>

                <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                  <button
                    onClick={() => navigate(-1)}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 transition font-semibold text-slate-900"
                  >
                    <ArrowLeft className="h-5 w-5" />
                    {t("error.goBack", "Go Back")}
                  </button>

                  <Link
                    to="/"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-blue-600 text-white font-semibold shadow-sm hover:bg-blue-700 transition focus:outline-none focus:ring-4 focus:ring-blue-200/70"
                  >
                    <Home className="h-5 w-5" />
                    {t("error.backHome", "Back to Home")}
                  </Link>
                </div>

                <div className="mt-10 pt-6 border-t border-slate-200">
                  <p className="text-sm text-slate-600 mb-4">{t("error.needHelp", "Need immediate assistance?")}</p>
                  <div className="flex flex-wrap gap-3 justify-center">
                    <a
                      href={SOCIAL.whatsapp}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-green-50 text-green-700 hover:bg-green-100 transition text-sm font-medium border border-green-200"
                    >
                      <MessageCircle className="h-4 w-4" />
                      WhatsApp
                    </a>
                    <a
                      href="tel:0524657778"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-50 text-blue-700 hover:bg-blue-100 transition text-sm font-medium border border-blue-200"
                    >
                      <PhoneCall className="h-4 w-4" />
                      052-465-7778
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* end card */}
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <footer className="border-t border-slate-800 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-10 w-10 shrink-0 rounded-2xl bg-slate-900 border border-slate-800 flex items-center justify-center shadow-sm">
                <img src={logo} alt="SmartBusiness" className="h-5 w-auto" />
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-slate-100 truncate">{t("homepage.brand", "SmartBusiness")}</div>
                <div className="text-sm text-slate-300">
                  {t("homepage.footer.developedBy", "Developed by")}{" "}
                  <span className="text-slate-100 font-semibold">Vynix</span>
                </div>
              </div>
            </div>

            <div className="md:justify-self-center">
              <div className="text-sm font-semibold mb-3 text-slate-100">{t("homepage.footer.social", "Social media")}</div>
              <div className="flex flex-wrap gap-3">
                <a
                  href={SOCIAL.facebook}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-800 bg-slate-900/40 hover:bg-slate-900 transition text-sm text-slate-200"
                >
                  <Facebook className="h-4 w-4" />
                  Facebook
                </a>
                <a
                  href={SOCIAL.instagram}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-800 bg-slate-900/40 hover:bg-slate-900 transition text-sm text-slate-200"
                >
                  <Instagram className="h-4 w-4" />
                  Instagram
                </a>
                <a
                  href={SOCIAL.tiktok}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-800 bg-slate-900/40 hover:bg-slate-900 transition text-sm text-slate-200"
                >
                  <Video className="h-4 w-4" />
                  TikTok
                </a>
                <a
                  href={SOCIAL.whatsapp}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-800 bg-slate-900/40 hover:bg-slate-900 transition text-sm text-slate-200"
                >
                  <MessageCircle className="h-4 w-4" />
                  WhatsApp
                </a>
              </div>
            </div>

            <div className={`${isRTL ? "md:justify-self-start" : "md:justify-self-end"}`}>
              <div className="text-sm font-semibold mb-3 text-slate-100">{t("homepage.footer.contact", "Contact us")}</div>
              <a
                href="tel:0524657778"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-800 bg-slate-900/40 hover:bg-slate-900 transition text-slate-100 font-semibold"
              >
                <PhoneCall className="h-4 w-4" />
                0524657778
              </a>
              <div className="mt-3 text-xs text-slate-400">
                © {new Date().getFullYear()} {t("homepage.brand", "SmartBusiness")}
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}